// Backend Observation.Status values we care about on the frontend.
export const OBSERVATION_STATUS = {
  DRAFT: "draft",
  SCHEDULED: "scheduled",
  IN_PROGRESS: "in_progress",
  SUBMITTED: "submitted",
  REVIEWED: "reviewed",
  SHARED: "shared",
  ACKNOWLEDGED: "acknowledged",
  CANCELLED: "cancelled",
};

// Statuses that mean an observation has been finalised and contributes to scores
// and analytics. Mirrors the backend `Observation.FINALIZED_STATUSES` in
// observations/models/observation.py.
export const FINALIZED_STATUSES = new Set([
  OBSERVATION_STATUS.SUBMITTED,
  OBSERVATION_STATUS.SHARED,
  OBSERVATION_STATUS.ACKNOWLEDGED,
  OBSERVATION_STATUS.REVIEWED,
]);

// A visit that exists but has not been written up yet — what «Назначено мне»
// lists and what `?visit=` conduct mode can still fill in.
export const OPEN_STATUSES = new Set([
  OBSERVATION_STATUS.SCHEDULED,
  OBSERVATION_STATUS.IN_PROGRESS,
]);

export const OBSERVATION_TYPE = {
  OFFICIAL: "official",
  PEER: "peer",
  SELF: "self",
};

// UI-only labels for the teacher dashboard's upcoming list — derived in
// decorateUpcoming from backend status + confirmed_at + direction. The state
// space is honest about who (if anyone) still owes an action:
//   PENDING    incoming peer request — YOU must confirm/decline
//   AWAITING   outgoing peer request — waiting on the OTHER teacher
//   CONFIRMED  peer visit both sides agreed (confirmed_at set)
//   SCHEDULED  official/self visit — no confirmation step exists
//   DECLINED / CANCELLED — closed visits
export const DISPLAY_STATUS = {
  PENDING: "pending",
  AWAITING: "awaiting",
  CONFIRMED: "confirmed",
  SCHEDULED: "scheduled",
  DECLINED: "declined",
  CANCELLED: "cancelled",
};

// Single source of truth for how each display status is presented — i18n label
// key, light badge (white cards in the list/calendar) and the dark-header badge
// (detail modal). Centralised so adding a status means editing one table, not a
// ternary in every component (mirrors the SCORE_TIERS table convention).
export const DISPLAY_STATUS_META = {
  [DISPLAY_STATUS.CONFIRMED]: {
    labelKey: "upcoming.statusConfirmed",
    badge: "bg-emerald-100 text-emerald-700",
    badgeOnDark: "bg-emerald-500/20 border-emerald-500/40 text-emerald-200",
  },
  [DISPLAY_STATUS.PENDING]: {
    labelKey: "upcoming.statusPending",
    badge: "bg-amber-100 text-amber-700",
    badgeOnDark: "bg-amber-500/20 border-amber-500/40 text-amber-200",
  },
  [DISPLAY_STATUS.AWAITING]: {
    labelKey: "upcoming.statusAwaiting",
    badge: "bg-amber-100 text-amber-700",
    badgeOnDark: "bg-amber-500/20 border-amber-500/40 text-amber-200",
  },
  [DISPLAY_STATUS.SCHEDULED]: {
    labelKey: "upcoming.statusScheduled",
    badge: "bg-slate-100 text-slate-600",
    badgeOnDark: "bg-white/15 border-white/30 text-white",
  },
  [DISPLAY_STATUS.DECLINED]: {
    labelKey: "upcoming.statusDeclined",
    badge: "bg-rose-100 text-rose-700",
    badgeOnDark: "bg-rose-500/20 border-rose-500/40 text-rose-200",
  },
  [DISPLAY_STATUS.CANCELLED]: {
    labelKey: "upcoming.statusCancelled",
    badge: "bg-rose-100 text-rose-700",
    badgeOnDark: "bg-rose-500/20 border-rose-500/40 text-rose-200",
  },
};

// Pending is the neutral fallback for any unrecognised status.
export const displayStatusMeta = (s) =>
  DISPLAY_STATUS_META[s] || DISPLAY_STATUS_META[DISPLAY_STATUS.PENDING];

// Backend Observation.Closure — distinguishes the two ways a visit reaches
// status=cancelled (the observed teacher declining vs the requester withdrawing).
export const CLOSURE = {
  DECLINED: "declined",
  CANCELLED: "cancelled",
};

export const DIRECTION = {
  INCOMING: "incoming",
  OUTGOING: "outgoing",
};

// Matches the backend Notification.Type enum.
export const NOTIFICATION_TYPE = {
  PEER_VISIT_REQUESTED: "peer_visit_requested",
  PEER_VISIT_CONFIRMED: "peer_visit_confirmed",
  PEER_VISIT_DECLINED: "peer_visit_declined",
  VISIT_RESCHEDULED: "visit_rescheduled",
  VISIT_CANCELLED: "visit_cancelled",
  OFFICIAL_VISIT_SCHEDULED: "official_visit_scheduled",
  // Sent to whoever was named main observer on a visit somebody else booked.
  // OFFICIAL_VISIT_SCHEDULED reaches only the observed teacher, so before this
  // existed an evaluator learned of their own assignment from nobody.
  VISIT_ASSIGNED: "visit_assigned",
  OBSERVATION_SUBMITTED: "observation_submitted",
  OBSERVATION_SHARED: "observation_shared",
  // Support answered one of the user's tickets; carries
  // `related_support_ticket` (the ticket uuid → /support/:uuid).
  SUPPORT_REPLY: "support_reply",
};

// School day is period-indexed P1..MAX_PERIOD (mirrors core.constants.MAX_LESSON_PERIOD).
export const MAX_PERIOD = 8;
export const PERIOD_CHOICES = Array.from({ length: MAX_PERIOD }, (_, i) => i + 1);
